/**
 * DictionaryContext
 * 
 * 管理字典數據的 Context
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { checkAndUpdateDictionaries, forceRefreshDictionaries } from '../services/dictionaryService';
import logger from '../utils/logger';
import { debugLog, checkEnvironment } from '../utils/dictionaryDebug';
import appInfo from '../../package.json';
import { FORCE_DICT_CHECK_ON_NEXT_LOAD_KEY } from '../constants/updateKeys';

const DictionaryContext = createContext(null);
const LOCAL_DICT_UPDATE_TIME = new Date('2024-01-01T00:00:00');
const DICTIONARY_SYNCED_APP_VERSION_KEY = 'dictionarySyncedAppVersion';
const CURRENT_APP_VERSION = appInfo.version || 'unknown';
const AUTO_REFRESH_INTERVAL_MS = (() => {
  const envValue = parseInt(process.env.REACT_APP_DICT_AUTO_REFRESH_INTERVAL_MINUTES || '720', 10);
  if (Number.isFinite(envValue) && envValue > 0) {
    return envValue * 60 * 1000;
  }
  return 720 * 60 * 1000;
})();

const resolveDictionaryLastUpdate = (dataSource) => {
  if (!dataSource) return new Date();

  if (dataSource.type === 'local') {
    return LOCAL_DICT_UPDATE_TIME;
  }

  if (dataSource.versionUpdatedAt) {
    const parsedDate = new Date(dataSource.versionUpdatedAt);
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate;
    }
  }

  return new Date();
};

export const DictionaryProvider = ({ children }) => {
  const [dictionaries, setDictionaries] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [dataSource, setDataSource] = useState(null);
  const [loadingProgress, setLoadingProgress] = useState({
    message: '',
    step: '',
    progress: 0,
  });

  const shouldForceCheckByAppVersion = () => {
    try {
      const lastSyncedVersion = localStorage.getItem(DICTIONARY_SYNCED_APP_VERSION_KEY);
      const forceCheckFlag = localStorage.getItem(FORCE_DICT_CHECK_ON_NEXT_LOAD_KEY) === '1';
      return forceCheckFlag || lastSyncedVersion !== CURRENT_APP_VERSION;
    } catch {
      return false;
    }
  };

  const markAppVersionSynced = () => {
    try {
      localStorage.setItem(DICTIONARY_SYNCED_APP_VERSION_KEY, CURRENT_APP_VERSION);
      localStorage.removeItem(FORCE_DICT_CHECK_ON_NEXT_LOAD_KEY);
    } catch {
      // ignore localStorage write errors
    }
  };

  // 初始化字典
  useEffect(() => {
    const initDictionaries = async () => {
      try {
        setIsLoading(true);
        setError(null);
        
        // 檢查環境配置
        const env = checkEnvironment();
        debugLog('DictionaryContext 初始化', env);
        
        // 設置初始進度（使用英文，因為 LanguageContext 可能還沒初始化）
        setLoadingProgress({
          message: env.configured ? 'Checking database version...' : 'Loading local data...',
          step: 'checking',
          progress: 10,
        });
        
        const forceCheck = shouldForceCheckByAppVersion();
        const result = await checkAndUpdateDictionaries(forceCheck);
        
        // 更新進度
        if (result.dataSource?.type === 'supabase') {
          setLoadingProgress({
            message: 'Downloading database data...',
            step: 'downloading',
            progress: 50,
          });
        } else {
          setLoadingProgress({
            message: 'Loading local data...',
            step: 'loading',
            progress: 70,
          });
        }
        setDictionaries(result.dictionaries);
        setDataSource(result.dataSource);
        if (result.dataSource?.type === 'supabase' && !result.dataSource?.offline) {
          markAppVersionSynced();
        }
        
        setLastUpdate(resolveDictionaryLastUpdate(result.dataSource));
        
        const dictNames = result.dictionaries ? Object.keys(result.dictionaries).join(', ') : 'none';
        logger.info(`Dictionaries loaded successfully: ${dictNames}`);
        debugLog('字典加載完成', { 
          count: result.dictionaries ? Object.keys(result.dictionaries).length : 0, 
          names: dictNames,
          dataSource: result.dataSource
        });
        
        // 完成進度
        setLoadingProgress({
          message: 'Loading complete',
          step: 'complete',
          progress: 100,
        });
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Unknown error';
        logger.error('Failed to load dictionaries:', err);
        debugLog('字典加載失敗', errorMsg);
        setError(errorMsg);
        // 設置默認數據源
        const env = checkEnvironment();
        setDataSource({
          type: 'local',
          configured: env.configured,
          url: env.configured ? env.supabaseUrl : null,
          reason: errorMsg
        });
      } finally {
        // 延遲一點再隱藏進度，讓用戶看到完成狀態
        setTimeout(() => {
          setIsLoading(false);
          setLoadingProgress({
            message: '',
            step: '',
            progress: 0,
          });
        }, 300);
      }
    };

    initDictionaries();
  }, []);

  // 刷新字典
  const refreshDictionaries = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      const result = await forceRefreshDictionaries();
      setDictionaries(result.dictionaries);
      setDataSource(result.dataSource);
      if (result.dataSource?.type === 'supabase' && !result.dataSource?.offline) {
        markAppVersionSynced();
      }
      
      setLastUpdate(resolveDictionaryLastUpdate(result.dataSource));
      
      logger.info('Dictionaries refreshed successfully');
      return true;
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Unknown error';
      logger.error('Failed to refresh dictionaries:', err);
      setError(errorMsg);
      // 設置默認數據源
      const env = checkEnvironment();
      setDataSource({
        type: 'local',
        configured: env.configured,
        url: env.configured ? env.supabaseUrl : null,
        reason: errorMsg
      });
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // 背景自動更新字典（定時 + 回到前景時）
  useEffect(() => {
    if (!dictionaries) return undefined;

    let isCancelled = false;

    const checkUpdatesInBackground = async () => {
      try {
        const result = await checkAndUpdateDictionaries(false);

        if (isCancelled || !result || !result.dictionaries) return;

        // 背景檢查時，避免因臨時網絡波動把已載入字典回退到本地
        if (result.dataSource?.type !== 'supabase') {
          return;
        }

        setDictionaries(result.dictionaries);
        setDataSource(result.dataSource);
        setLastUpdate(resolveDictionaryLastUpdate(result.dataSource));
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          logger.warn('Background dictionary refresh failed:', err);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkUpdatesInBackground();
      }
    };

    const intervalId = setInterval(checkUpdatesInBackground, AUTO_REFRESH_INTERVAL_MS);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      isCancelled = true;
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [dictionaries]);

  const value = {
    dictionaries,
    isLoading,
    error,
    lastUpdate,
    dataSource,
    loadingProgress,
    refreshDictionaries
  };

  return (
    <DictionaryContext.Provider value={value}>
      {children}
    </DictionaryContext.Provider>
  );
};

export const useDictionary = () => {
  const context = useContext(DictionaryContext);
  if (!context) {
    throw new Error('useDictionary must be used within DictionaryProvider');
  }
  return context;
};
